import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance, {
  checkTokenValidity,
  CheckUserExpiry,
  clearAuthData,
  isJwtAuthFailure,
} from "../../components/content/axiosConfig";
import PondDetailWorkflowTabs from "../../components/custom/PondDetailWorkflowTabs";
import PondDetailWorkflowPageHeader from "../../components/custom/PondDetailWorkflowPageHeader";
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import showErrorToast from "../../components/toast/toast";
import {
  formatDisplayDate as formatDateForMobile,
  formatDisplayDateTime as formatDateTime,
} from "../../utils/formatDisplayDate";

const PondNoteViaPondDetail = () => {
    const { id } = useParams();
    const [visitName, setVisitName] = useState("");

    const CheckExpiry = useCallback(async () => {
        await CheckUserExpiry();
    }, []);

    const fetchPondNotesPage = useCallback(async ({ search, page, limit }) => {
        try {
            return await axiosInstance.get(`/api/PondNote/GetAllNotesViaPondDetailID/${id}`, {
                params: { search, page, limit },
            });
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                showErrorToast("Session expired. Please log in again.");
                return { data: { data: [], total: 0 } };
            }
            showErrorToast(error.response?.data?.message || "Failed to fetch pond notes");
            throw error;
        }
    }, [id]);

    const {
        rows: notes,
        totalCount,
        searchTerm,
        setSearchTerm,
        currentPage,
        setCurrentPage,
        isLoading,
        entriesPerPage,
    } = useServerPaginatedList(fetchPondNotesPage, { enabled: !!id });

    useEffect(() => {
        checkTokenValidity();
        CheckExpiry();
    }, [CheckExpiry]);

    useEffect(() => {
        if (!id) return;
        axiosInstance
            .get(`/api/PondDetail/GetPondDetailByID`, { params: { id } })
            .then((res) => setVisitName(res.data?.Name || ""))
            .catch(() => setVisitName(""));
    }, [id]);

    if (isLoading && notes.length === 0) {
        return <Preloader />;
    }

    return (
        <div className="donezo-page donezo-workflow-page py-3">
            <div className="container-fluid px-3 px-md-4">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4">
                    <div className="mb-3 mb-md-0 flex-grow-1" style={{ minWidth: 0 }}>
                        <PondDetailWorkflowPageHeader
                            pondDetailId={id}
                            section="notes"
                            detailName={visitName}
                            noBottomMargin
                        />
                    </div>
                </div>
                <PondDetailWorkflowTabs id={id} active="notes" />
                <div className="card border-0 shadow-sm donezo-workflow-card">
                    <div className="tab-content p-3 p-md-4">
                        <div className="input-group mb-3">
                            <div className="flex-fill position-relative">
                                <div className="input-group">
                                    <div
                                        className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                                        style={{ zIndex: 10 }}
                                    >
                                        <i className="fa fa-search opacity-5"></i>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control px-35px bg-light"
                                        placeholder="Search Notes..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="d-none d-md-block">
                            <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Pond Name</th>
                                        <th>Note</th>
                                        <th>Update Date</th>
                                        <th>By Staff</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan={4} className="text-center text-muted py-5">
                                                Loading…
                                            </td>
                                        </tr>
                                    ) : (
                                        notes.map((web) => (
                                            <tr key={web.PondNoteID}>
                                                <td>{web.PondName}</td>
                                                <td title={web.PondNote}>{web.PondNote.length > 50 ? web.PondNote.slice(0, 50) + '...' : web.PondNote}</td>
                                                <td>{formatDateTime(web.UpdatedAt)}</td>
                                                <td>{web.StaffName}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="d-md-none">
                            {isLoading ? (
                                <div className="text-center text-muted py-4">Loading…</div>
                            ) : (
                                notes.map((web) => (
                                    <div key={web.PondNoteID} className="card mb-3 shadow-sm donezo-workflow-mobile-card">
                                        <div className="card-body p-3">
                                            <h5 className="card-title mb-0">{web.PondName}</h5>
                                            <div className="row mt-2">
                                                <div className="col-12">
                                                    <p className="mb-1"><small className="text-muted">Note:</small> {web.PondNote}</p>
                                                </div>
                                                <div className="col-6">
                                                    <p className="mb-1"><small className="text-muted">Updated:</small> {formatDateForMobile(web.UpdatedAt)}</p>
                                                </div>
                                                <div className="col-6">
                                                    <p className="mb-1"><small className="text-muted">By:</small> {web.StaffName}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <ListPagination
                            currentPage={currentPage}
                            totalCount={totalCount}
                            entriesPerPage={entriesPerPage}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PondNoteViaPondDetail;
